/**
 * IANA time zone rules applied on top of the runtime's own time zone data (Intl, backed by ICU).
 *
 * The runtimes this code meets carry different tz releases. On 6 October 2026 the deployed Vercel
 * runtime said Africa/Casablanca is UTC+0, as tzdata 2026c (8 July 2026) has it since Morocco went
 * back to permanent GMT on 20 September 2026 at 02:00 (Decree No. 2.26.530 of 25 June 2026), while
 * local Node 24.14 (ICU 78.2, tz 2025c), Open-Meteo and Supabase Postgres 17.6 still said UTC+1.
 * tzdata 2026b to 2026e also end clock changes in four Canadian zones from 1 November 2026, which
 * the same stale runtimes will get wrong from that date. Every zone conversion that decides a
 * stored or scored local day, hour or offset asks this module first, so all machines and browsers
 * agree with tzdata 2026e for these zones whatever their own ICU data holds.
 *
 * Each rule is a permanent fixed offset from the first instant at which an older release (2025c,
 * the oldest in use) disagrees with 2026e; before that instant every release since 2018h agrees and
 * the runtime's data is used unchanged. Every other zone is always the runtime's.
 *
 * Sources: https://data.iana.org/time-zones/tzdb/NEWS (2026b, 2026c, 2026d, 2026e), the `africa`,
 * `northamerica` and `backward` files of tzdata 2026e; evidence in
 * pw-evidence/2026-10-06-morocco-tz/. Pure and isomorphic: no I/O, no Node APIs.
 */

export const ZONE_RULES_TZDATA_RELEASE = "2026e";

const HOUR_SECONDS = 3600;
const DAY_MS = 86_400_000;

export type PermanentOffsetRule = Readonly<{
  /** The canonical zone, then the backward-compatible links that follow it. */
  zones: readonly string[];
  /** First instant (UTC milliseconds) from which the zone keeps `offsetSeconds` for good. */
  fromUtcMs: number;
  /** UTC offset in seconds, east positive. */
  offsetSeconds: number;
  /**
   * The other offset older releases give after `fromUtcMs` (Morocco's +01 outside Ramadan, the
   * Canadian winter offsets). Older data gives this or `offsetSeconds`, nothing else, so a provider
   * on stale tz data (Open-Meteo, 6 October 2026) labels at one of the two.
   */
  previousOffsetSeconds: number;
  /** The tz release that made the change. */
  release: string;
}>;

export const PERMANENT_OFFSET_RULES: readonly PermanentOffsetRule[] = Object.freeze([
  // Zone Africa/Casablanca ... 1:00 Morocco %z 2026 Sep 20 2:00 / 0:00 - %z (El_Aaiun the same):
  // 02:00 local at +01 is 01:00Z. Before it the runtime's Morocco rules (+01, +00 in Ramadan) stand.
  Object.freeze({ zones: Object.freeze(["Africa/Casablanca", "Africa/El_Aaiun"]), fromUtcMs: Date.UTC(2026, 8, 20, 1), offsetSeconds: 0, previousOffsetSeconds: HOUR_SECONDS, release: "2026c" }),
  // British Columbia: PDT until 2026 Nov 1 2:00, then MST (-07) for good. 02:00 PDT is 09:00Z.
  Object.freeze({ zones: Object.freeze(["America/Vancouver", "Canada/Pacific"]), fromUtcMs: Date.UTC(2026, 10, 1, 9), offsetSeconds: -7 * HOUR_SECONDS, previousOffsetSeconds: -8 * HOUR_SECONDS, release: "2026b" }),
  // Alberta: MDT until 2026 Nov 1 2:00, then CST (-06) for good. 02:00 MDT is 08:00Z.
  Object.freeze({ zones: Object.freeze(["America/Edmonton", "Canada/Mountain", "America/Yellowknife"]), fromUtcMs: Date.UTC(2026, 10, 1, 8), offsetSeconds: -6 * HOUR_SECONDS, previousOffsetSeconds: -7 * HOUR_SECONDS, release: "2026c" }),
  // Northwest Territories (Inuvik): MDT until 2026 Nov 1 2:00, then CST (-06).
  Object.freeze({ zones: Object.freeze(["America/Inuvik"]), fromUtcMs: Date.UTC(2026, 10, 1, 8), offsetSeconds: -6 * HOUR_SECONDS, previousOffsetSeconds: -7 * HOUR_SECONDS, release: "2026d" }),
  // Manitoba: CDT until 2026 Nov 1 2:00, then EST (-05). 02:00 CDT is 07:00Z. 2026e's `backward`
  // links America/Rainy_River to it too (NEWS warns north-western Ontario may later diverge).
  Object.freeze({ zones: Object.freeze(["America/Winnipeg", "Canada/Central", "America/Rainy_River"]), fromUtcMs: Date.UTC(2026, 10, 1, 7), offsetSeconds: -5 * HOUR_SECONDS, previousOffsetSeconds: -6 * HOUR_SECONDS, release: "2026e" }),
]);

// Intl accepts zone names in any case; so does this lookup.
const RULE_BY_ZONE: ReadonlyMap<string, PermanentOffsetRule> = new Map(
  PERMANENT_OFFSET_RULES.flatMap((rule) => rule.zones.map((zone) => [zone.toLowerCase(), rule] as const)),
);

// Zone names seen so far, as given, so the hot paths (every slot of every frame) skip the case fold.
const RULE_BY_NAME = new Map<string, PermanentOffsetRule | null>();

function ruleFor(timeZone: string): PermanentOffsetRule | undefined {
  if (typeof timeZone !== "string") return undefined;
  let rule = RULE_BY_NAME.get(timeZone);
  if (rule === undefined) {
    rule = RULE_BY_ZONE.get(timeZone.toLowerCase()) ?? null;
    if (RULE_BY_NAME.size < 4096) RULE_BY_NAME.set(timeZone, rule);
  }
  return rule ?? undefined;
}

/** The rule for `timeZone` (any case, links included), whatever the instant; undefined for every other zone. */
export function permanentOffsetRuleFor(timeZone: string): PermanentOffsetRule | undefined {
  return ruleFor(timeZone);
}

/** The rule's offset (seconds) when one governs `timeZone` at `utcMs`, else null (use the runtime's data). */
export function ruleOffsetSecondsAt(utcMs: number, timeZone: string): number | null {
  const rule = ruleFor(timeZone);
  return rule !== undefined && utcMs >= rule.fromUtcMs ? rule.offsetSeconds : null;
}

export type WallClock = Readonly<{ year: number; month: number; day: number; hour: number; minute: number; second: number }>;

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/**
 * The runtime's formatter for a zone (en-GB, 24-hour, whole seconds). Throws
 * `invalid IANA time zone "<name>"` for a name the runtime does not know.
 */
export function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  const existing = FORMATTERS.get(timeZone);
  if (existing) return existing;
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatter.formatToParts(new Date(0));
  } catch {
    throw new Error(`invalid IANA time zone ${JSON.stringify(timeZone)}`);
  }
  FORMATTERS.set(timeZone, formatter);
  return formatter;
}

/** The runtime's own wall clock of an instant in a zone, ignoring the rules. */
export function intlWallClock(utcMs: number, timeZone: string): WallClock {
  const parts = zoneFormatter(timeZone).formatToParts(new Date(utcMs));
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((candidate) => candidate.type === type);
    if (!part) throw new Error(`time zone formatter omitted ${type}`);
    return Number(part.value);
  };
  return {
    year: read("year"), month: read("month"), day: read("day"),
    hour: read("hour") % 24, minute: read("minute"), second: read("second"),
  };
}

function utcWallClock(utcMs: number): WallClock {
  const instant = new Date(Math.floor(utcMs / 1000) * 1000);
  return {
    year: instant.getUTCFullYear(), month: instant.getUTCMonth() + 1, day: instant.getUTCDate(),
    hour: instant.getUTCHours(), minute: instant.getUTCMinutes(), second: instant.getUTCSeconds(),
  };
}

/**
 * Wall clock (to the whole second) of an instant in a zone: the rule's fixed offset where one
 * governs, else the runtime's Intl. Throws for a zone name the runtime does not know.
 */
export function zonedWallClock(utcMs: number, timeZone: string): WallClock {
  const offset = ruleOffsetSecondsAt(utcMs, timeZone);
  if (offset === null) return intlWallClock(utcMs, timeZone);
  zoneFormatter(timeZone);
  return utcWallClock(utcMs + offset * 1000);
}

function wallClockMs(wall: WallClock): number {
  return Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
}

/** UTC offset in whole seconds of a zone at an instant (east positive), rules first. */
export function zoneOffsetSecondsAt(utcMs: number, timeZone: string): number {
  const offset = ruleOffsetSecondsAt(utcMs, timeZone);
  if (offset !== null) {
    zoneFormatter(timeZone);
    return offset;
  }
  const flooredMs = Math.floor(utcMs / 1000) * 1000;
  return Math.round((wallClockMs(intlWallClock(flooredMs, timeZone)) - flooredMs) / 1000);
}

/** Local calendar date (YYYY-MM-DD) of an instant in a zone, rules first. */
export function zonedDate(utcMs: number, timeZone: string): string {
  const wall = zonedWallClock(utcMs, timeZone);
  return `${String(wall.year).padStart(4, "0")}-${String(wall.month).padStart(2, "0")}-${String(wall.day).padStart(2, "0")}`;
}

/**
 * The offsets (seconds) a third party may label `timeZone` at `utcMs`: the zone's offset, plus the
 * rule's previous offset where a rule governs, since a provider on stale tz data (Open-Meteo
 * labelled Morocco at +01 on 6 October 2026) still uses it. Ascending, one or two values.
 */
export function providerLabelOffsetsSecondsAt(utcMs: number, timeZone: string): number[] {
  const rule = ruleFor(timeZone);
  if (rule === undefined || !(utcMs >= rule.fromUtcMs)) return [zoneOffsetSecondsAt(utcMs, timeZone)];
  zoneFormatter(timeZone);
  return [...new Set([rule.offsetSeconds, rule.previousOffsetSeconds])].sort((left, right) => left - right);
}

/** An IANA fixed-offset zone for a whole-hour offset: Etc/GMT for 0, Etc/GMT+7 for UTC-7 (POSIX signs). */
export function fixedOffsetZone(offsetSeconds: number): string {
  if (!Number.isInteger(offsetSeconds) || offsetSeconds % HOUR_SECONDS !== 0 || Math.abs(offsetSeconds) > 14 * HOUR_SECONDS) {
    throw new Error(`no fixed-offset zone for ${offsetSeconds} seconds`);
  }
  const hours = offsetSeconds / HOUR_SECONDS;
  return hours === 0 ? "Etc/GMT" : `Etc/GMT${hours > 0 ? "-" : "+"}${Math.abs(hours)}`;
}

const HOLDS_FROM = new Map<PermanentOffsetRule, number>();

/**
 * The first instant (UTC milliseconds) from which the rule's zone already keeps the rule's offset
 * without a break up to the rule's start, and so for good: the rule's own start when the offset
 * changes there (Morocco, +01 until 2026-09-20T01:00Z), else the clock change before it (British
 * Columbia's PDT is -07 from 2026-03-08T10:00Z, the same offset its permanent MST keeps from
 * 1 November). Before the start every tz release agrees, so the runtime's data decides. A request
 * zone of the rule's fixed offset (fixedOffsetZone) labels every instant from here as the zone does.
 */
export function fixedOffsetHoldsFromUtcMs(rule: PermanentOffsetRule): number {
  const cached = HOLDS_FROM.get(rule);
  if (cached !== undefined) return cached;
  const zone = rule.zones[0];
  const holds = (utcMs: number): boolean => zoneOffsetSecondsAt(utcMs, zone) === rule.offsetSeconds;
  let from = rule.fromUtcMs;
  for (let days = 0; days < 400 && holds(from - DAY_MS); days++) from -= DAY_MS;
  for (let hours = 0; hours < 24 && holds(from - HOUR_SECONDS * 1000); hours++) from -= HOUR_SECONDS * 1000;
  HOLDS_FROM.set(rule, from);
  return from;
}

const RUNTIME_AGREES = new Map<PermanentOffsetRule, boolean>();

/** Whether the runtime's own data already holds a rule (checked two weeks after it starts, once). */
function runtimeAgreesWith(rule: PermanentOffsetRule): boolean {
  const cached = RUNTIME_AGREES.get(rule);
  if (cached !== undefined) return cached;
  let agrees: boolean;
  try {
    const probeMs = rule.fromUtcMs + 14 * DAY_MS;
    agrees = Math.round((wallClockMs(intlWallClock(probeMs, rule.zones[0])) - probeMs) / 1000) === rule.offsetSeconds;
  } catch {
    agrees = false;
  }
  RUNTIME_AGREES.set(rule, agrees);
  return agrees;
}

/**
 * The `timeZone` to hand Intl when formatting `utcMs` for display in `timeZone`: the zone itself,
 * unless a rule governs that instant and this runtime's data lacks it (a stale browser or Node),
 * then the fixed-offset zone of the rule's offset, so the wall clock shown is tzdata 2026e's.
 * Format each instant with its own call: a formatter built for one instant is right only for
 * instants on the same side of the rule's start.
 */
export function intlTimeZoneAt(timeZone: string, utcMs: number): string {
  const rule = ruleFor(timeZone);
  if (rule === undefined || !(utcMs >= rule.fromUtcMs) || runtimeAgreesWith(rule)) return timeZone;
  return fixedOffsetZone(rule.offsetSeconds);
}

/** The canonical zones whose rules this runtime's own Intl data does not hold. */
export function runtimeZonesLackingRules(): string[] {
  return PERMANENT_OFFSET_RULES.filter((rule) => !runtimeAgreesWith(rule)).map((rule) => rule.zones[0]);
}

const TZ_RELEASE_RE = /^(\d{4})([a-z]+)$/u;

/**
 * Order of two tz release names ("2025c", "2026e"): negative when `left` is older, 0 when the
 * same, positive when newer; null when either is not a release name.
 */
export function compareTzReleases(left: string, right: string): number | null {
  const a = TZ_RELEASE_RE.exec(left);
  const b = TZ_RELEASE_RE.exec(right);
  if (!a || !b) return null;
  if (a[1] !== b[1]) return Number(a[1]) - Number(b[1]);
  if (a[2].length !== b[2].length) return a[2].length - b[2].length;
  return a[2] < b[2] ? -1 : a[2] > b[2] ? 1 : 0;
}

export type ZoneRulesStatus = Readonly<{
  /** The tz release this table follows (ZONE_RULES_TZDATA_RELEASE). */
  release: string;
  /** The runtime's own tz release, or null when it does not say. */
  runtimeTz: string | null;
  /** Rules the runtime's data lacks because its release predates them (or is unknown): expected, the rules apply. */
  runtimeLacks: readonly string[];
  /**
   * Rules a runtime at this table's release or newer disagrees with: a later tz release changed the
   * zone again, or the table is wrong. The rules still override the runtime, so the table must be
   * reviewed against that release before it shadows newer, correct data.
   */
  runtimeDisagrees: readonly string[];
}>;

/** How this runtime's own data stands against the rules (health diagnostics and the CI check in lib/__tests__/time-zone-rules.test.ts). */
export function zoneRulesStatus(runtimeTz: string | null | undefined): ZoneRulesStatus {
  const tz = typeof runtimeTz === "string" && runtimeTz !== "" ? runtimeTz : null;
  const order = tz === null ? null : compareTzReleases(tz, ZONE_RULES_TZDATA_RELEASE);
  const differing = runtimeZonesLackingRules();
  const atOrAfter = order !== null && order >= 0;
  return {
    release: ZONE_RULES_TZDATA_RELEASE,
    runtimeTz: tz,
    runtimeLacks: atOrAfter ? [] : differing,
    runtimeDisagrees: atOrAfter ? differing : [],
  };
}
